/**
 * Feature registry — single source of truth for all feature metadata.
 * Used by state defaults, migrations, UI labels, and exporter logic.
 * NO file payloads here — those live in feature-packs.js (Phase 3+).
 *
 * Dependency rule types:
 *   requiresAll:  [...keys] — ALL listed features must be enabled
 *   requiresAny:  [...keys] — at least ONE listed feature must be enabled
 *   softRequires: [...keys] — shows a warning but doesn't block export
 */

export const FEATURES = [
  {
    key: 'cms',
    label: 'CMS',
    description: 'Decap CMS admin UI + GitHub OAuth',
    default: true,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'forms',
    label: 'Form Handling',
    description: 'Contact form API (Cloudflare Function + D1)',
    default: true,
    usesD1: true,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'pwa',
    label: 'PWA',
    description: 'Service Worker + offline page',
    default: true,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'pwaInstall',
    label: 'PWA Install Prompt',
    description: 'Install promotion for Chromium, Android, iOS and macOS Safari (requires PWA)',
    // Renders an extras slot under this toggle in the Export Settings modal,
    // filled by ui/pwa-install-settings.js. Keeps the modal registry-driven
    // rather than growing a hardcoded branch per feature.
    hasExtras: true,
    default: true,
    usesD1: false,
    usesR2: false,
    requiresAll: ['pwa'],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'sitemap',
    label: 'Sitemap',
    description: 'XML sitemap + robots.txt generation',
    default: true,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'i18n',
    // 1.1.0 (A6): the pack's exclusive output — the translated page copies and
    // its language-switcher header include — carries section root ids, which
    // the canonical-id flip renamed. Ownership is proven from emitted bytes, so
    // the change lands here even though the CAUSE is exporter-core.
    // 1.2.0 (C5): same shape again — the translated page copies carry the
    // per-page LCP image attributes, which now differ.
    // 1.2.1 (C6): art-directed translated-page heroes retain the original-src
    // preload because they are not replaced by the optimized shortcode.
    // 1.3.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.4.0 (IF-8): motion stamps (`data-pb-motion*`) now survive export. The pack
    // did not change; its exclusive output carries section markup and ownership
    // is proven from emitted bytes.
    // 1.5.0 (ingest fidelity closeout): canonical navigation markers now survive
    // cleanup in the pack's translated header output, restoring mobile toggles.
    // 1.7.0 (export readiness P9): the pack's attributed export-report.html now
    // carries the frozen snapshot's full audit rather than a headless empty audit.
    // 1.9.0 (EA-2): the SAME shape a fifth time — the pack's exclusive translated
    // page copies carry page-section markup, which now carries builder entrance
    // attributes. The pack itself did not change; ownership is proven from
    // emitted bytes, and this is the ONLY pack the ratchet forced: every other
    // pack's move was aggregate-only and is attributed to exporter-core.
    // 1.11.0 (AG-4): the SAME shape a sixth time — golden section H is a
    // behaviour-stamped widget on the About page, so the pack's translated copy
    // of that page (`src/pages/es/about.html`) carries the stamp. Again the
    // ONLY pack the ratchet forced; the others' changed paths were README /
    // LLM docs, which are aggregate and belong to exporter-core. The gate said
    // so itself, in those words, rather than being reasoned about.
    //
    // 1.11.0 -> 1.12.0 (AG-6): golden section H gained a stamped `.swiper`
    // host, and the translated About page carries it. Same reason, same gate,
    // same words: "Own output changed, version did not: [i18n]".
    //
    // 1.12.0 -> 1.13.0 (AG-12): golden section H gained an `aria-toggle-open`
    // nav and mobile-nav.js changed; the translated About page carries both.
    // Same gate, same words.
    version: '1.13.0',
    label: 'Multi-Language',
    description: 'i18n with language switcher, hreflang SEO tags, and starter locales (EN + ES)',
    default: false,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'pushNotifications',
    // 1.0.1: shared setup-guide output now warns that confirmation receipts
    // echo submitted content and therefore make Turnstile materially stronger.
    // 1.1.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.2.0 (IF-8): motion stamps (`data-pb-motion*`) now survive export. The pack
    // did not change; its exclusive output carries section markup and ownership
    // is proven from emitted bytes.
    // 1.4.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.5.0 (export readiness P9): attributed report bytes now carry the full
    // frozen-snapshot audit; ownership is proven by the release matrix.
    version: '1.6.0',
    label: 'Push Notifications',
    description: 'Web Push via VAPID + Cloudflare Workers (requires PWA)',
    default: false,
    usesD1: true,
    usesR2: false,
    requiresAll: ['pwa'],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'blog',
    // 1.1.0 (C5): the pack's exclusive output — the post/listing pages —
    // carries the per-page LCP image attributes, which now differ.
    // 1.2.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.3.0 (IF-8): motion stamps (`data-pb-motion*`) now survive export. The pack
    // did not change; its exclusive output carries section markup and ownership
    // is proven from emitted bytes.
    // 1.5.0 (export readiness P9): attributed report bytes now carry the full
    // frozen-snapshot audit; ownership is proven by the release matrix.
    version: '1.7.0',
    label: 'Blog',
    description: 'Blog with posts, TOC, social sharing, and featured posts sidebar',
    default: false,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: ['cms']
  },
  {
    key: 'cron',
    // 1.0.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.1.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.2.0 (ES-6): the scheduler gained a per-pack job seam. index.js merges
    // ESIGN_JOB_HANDLERS/DEFAULT_JOBS/RETIRED_JOB_IDS from jobs/esign.js, which
    // always exists (stub when e-sign is off), and wrangler.toml became a
    // function of the flags so the R2 binding appears only where it is used.
    // 1.3.0 (ES-7): the scheduler carries two more e-sign jobs (daily and
    // monthly audit anchoring). Its own code did not change; the job module it
    // bundles did, and ownership is proven from emitted bytes.
    // 1.4.0 (execution audit): reminder delivery is capped, link rotation is
    // failure-safe, provider message ids are retained, and signer-facing
    // Resend transport now lives in its planned flag-aware companion module.
    version: '1.5.0',
    label: 'Cron Scheduler',
    // Ships as a companion Worker beside the Pages site (scheduler/), because
    // cron triggers are a Workers-only feature. It declares its own email
    // secrets, so it no longer needs the forms feature to be on.
    description:
      'Scheduled jobs (health checks, cache purge, data cleanup) in a companion Cloudflare Worker',
    default: false,
    usesD1: true,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'shop',
    // 1.3.0 (A6): same cause as i18n — the cart-bearing header include is this
    // pack's exclusive output and its section ids moved with the flip.
    // 1.3.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.4.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.5.0 (IF-8): motion stamps (`data-pb-motion*`) now survive export. The pack
    // did not change; its exclusive output carries section markup and ownership
    // is proven from emitted bytes.
    // 1.6.0 (ingest fidelity closeout): canonical navigation markers now survive
    // cleanup in the pack's cart-bearing header output, restoring mobile toggles.
    // 1.8.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.9.0 (export readiness P9): attributed report bytes now carry the full
    // frozen-snapshot audit; ownership is proven by the release matrix.
    version: '1.10.0',
    label: 'E-Commerce',
    description: 'Product catalog, cart, and Stripe checkout',
    default: false,
    usesD1: true,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'portal',
    // 1.2.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.3.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.5.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.6.0 (ES-6): RESEND_API_KEY became target 'both' so the scheduler can send
    // e-sign reminders on the SAME transport as every other signer-facing
    // email. That changes the setup surfaces (.dev.vars, SETUP.html) this
    // pack contributes to; the pack itself did not change, and ownership is
    // proven from emitted bytes.
    // 1.7.0 (execution audit): the shared Resend sender returns the provider id
    // so signing delivery/bounce webhooks can join to durable email evidence.
    // 1.8.0 (export readiness P9): attributed report bytes now carry the full
    // frozen-snapshot audit; ownership is proven by the release matrix.
    version: '1.9.0',
    label: 'Client Portal',
    description: 'Authenticated client portal with dashboard, notifications, and magic link login',
    default: false,
    usesD1: true,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: ['forms']
  },
  {
    key: 'googleSignIn',
    // 1.0.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.1.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.2.0 (ES-6): RESEND_API_KEY became target 'both' so the scheduler can send
    // e-sign reminders on the SAME transport as every other signer-facing
    // email. That changes the setup surfaces (.dev.vars, SETUP.html) this
    // pack contributes to; the pack itself did not change, and ownership is
    // proven from emitted bytes.
    version: '1.3.0',
    label: 'Google Sign-in',
    description: 'Google Sign-in button and One Tap for Client Portal',
    default: false,
    // Client Portal already owns the D1 requirement. Keeping this false also
    // prevents a missing/undefined provider flag from independently enabling D1.
    usesD1: false,
    usesR2: false,
    requiresAll: ['portal'],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'messaging',
    // 1.2.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.3.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.4.0 (ES-6): RESEND_API_KEY became target 'both' so the scheduler can send
    // e-sign reminders on the SAME transport as every other signer-facing
    // email. That changes the setup surfaces (.dev.vars, SETUP.html) this
    // pack contributes to; the pack itself did not change, and ownership is
    // proven from emitted bytes.
    version: '1.5.0',
    label: 'Messaging',
    description: 'Real-time client messaging with reactions, GIF search, and typing indicators',
    default: false,
    usesD1: true,
    usesR2: true,
    requiresAll: ['portal'],
    requiresAny: [],
    softRequires: ['pushNotifications']
  },
  {
    key: 'esign',
    // 1.3.0 (ES-1): wave-2 schema — the pack's FIRST real runtime migrations.
    // It shipped `migrations: []` in all three routes, so no deployed e-sign
    // database had ever been migrated and any schema change was invisible until
    // the site 500'd. Also: RETURNING id on create (the re-query it replaces
    // returns the wrong row when a client is sent the same contract twice),
    // salted + rejection-sampled OTP, draft editing with immutability starting
    // at `sent` rather than `signed`, and both live surfaces' native dialogs
    // replaced. Version base is 1.2.0 from the concurrent canonical-id flip.
    // 1.4.0 (ES-2): hash chain v2 — recursive canonicalization, the canonical
    // payload stored verbatim, and actor/IP/user-agent/contract_id folded INTO
    // the hash (v1 left them outside it, so the audit trail's attribution was
    // rewritable while still verifying clean). v1 rows are never re-hashed;
    // they verify under v1 rules and the weakness is disclosed. Also: verify
    // now covers the document and the chain seal, an empty chain is invalid,
    // consent text is server-anchored and written verbatim into the chain,
    // rate limits reach verify-otp and submit, and the CRM tab finally shows
    // verification — nothing had ever called /verify.
    // 1.4.1: audit corrections for ES-1/ES-2. Submit is now cookie-backed
    // rather than bearer-session-id backed, the signer page can auto-recover a
    // verified session from the HttpOnly cookie, consent is a strict /consent
    // route with three confirmations + exact disclosure echo, and signer rate
    // limits use the plan-created esign_rate_limits table.
    // 1.4.2: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.5.0 (IF-7): the pack's exclusive output carries base.html, and IF-7 moved
    // critical CSS inline per route and deferred the full sheets there. The pack
    // itself did not change; ownership is proven from emitted bytes.
    // 1.7.0 (ES-3): the PDF pipeline. A contract is now rendered to PDF at SEND
    // (Cloudflare Browser Rendering REST — Pages cannot bind it), hashed, and
    // that hash is what the signature binds to; pdf-lib stamps the signature
    // in-Worker and appends a Certificate of Completion. Finalization is a
    // durable saga: the intent is recorded in pending_completions BEFORE the
    // work, completion is CLAIMED with a conditional UPDATE (appendIfMissing is
    // read-then-write and two concurrent finalizes both passed it), and R2
    // objects are write-once — mismatched bytes are refused and raised as an
    // anomaly rather than overwriting evidence. BROWSERLESS_API_KEY, declared
    // since the pack shipped and read by nothing, is gone.
    // 1.8.0 (ES-4): the contract builder. contract_templates rows are DATA in
    // each site's own database; the section renderer, the 17-field merge-token
    // registry, escaping, validation and version-on-edit are pack CODE. A
    // business's tailored agreement is therefore content, and esign@1.x -> 1.y
    // keeps flowing to every client site no matter how bespoke its contract is.
    // Editing an ACTIVE template creates version+1 and archives the old one,
    // because instances pin template_id and a signed contract must keep
    // resolving to the wording that was signed.
    // 1.8.1 (ES-4 follow-up): the CRM tab's merge-field scanner was emitting
    // /{{s*(...)s*}}/ — a single backslash is consumed by the builder template
    // literal it lives in, so the emitted regex matched a LITERAL 's' and found
    // nothing in '{{ client_name }}'. It happened to work unspaced, which is
    // why it looked fine. Caught by ESLint's no-useless-escape; the EMITTED
    // regex is now pinned by esign-template-tests.
    // 1.9.0 (ES-5): contract templates can be authored in PageStitch and SEEDED
    // into the exported site's contract_templates table. Still data — the site
    // owns the rows, seeding only inserts an absent (template_key, version), so
    // an owner's edit is never overwritten and an archived template is never
    // resurrected by a redeploy. One file in the export is state-derived, and
    // it is deterministic (sorted, no clock) because emitted bytes are diffed.
    // 1.10.0 (ES-6): the pack contributes four jobs to the cron scheduler —
    // reminders, expiry, completion retry and nightly chain verification. All
    // of it is recovery and upkeep, never the signing path, which is why cron
    // is a SOFT requirement and the CRM states plainly what is not automatic
    // when the scheduler is absent.
    // 1.11.0 (ES-7): audit anchoring. The chain lives in the same database it
    // protects, so a digest of it is published where that database's owner has
    // no control: a daily root to immutable R2 (claimed in D1 first, never
    // overwritten on mismatch) and a monthly Merkle root submitted to
    // OpenTimestamps, with the leaves published so anyone can re-derive it. The
    // OTS -> Bitcoin upgrade is a deliberate stub and both the API and the CRM
    // say the receipt is pending rather than implying an attestation.
    // 1.12.0 (ES-8): envelopes (several documents, ONE signing ceremony — not
    // multi-signer, which is a different feature) and dispute packets. The
    // packet RECOMPUTES verification rather than copying the stored flag, and
    // records absent members instead of omitting them, so a reader can tell
    // 'not produced' from 'withheld'. Wave 4 adds signing_envelopes plus
    // envelope_id/envelope_order/doc_role — doc_role being a forward-provision
    // so the deferred uploaded-PDF work needs no further fleet-wide migration.
    // 1.13.0 (execution audit): closes the incomplete ES-8 surfaces and hardens
    // R2 claims, completion recovery, artifact verification, expiry/reminders,
    // anchor retries, email replay processing, previews, and envelope UX.
    // 1.14.0 (export readiness P9): attributed report bytes now carry the full
    // frozen-snapshot audit; ownership is proven by the release matrix.
    // 1.16.0 (addon demo harness): the CRM contracts tab emitted LITERAL
    // merge-field delimiters — `'{{' + esc(t.name) + '}}'`, a `<code>` sample,
    // and an explanatory comment. The CRM shell ships as src/pages/crm.html and
    // the generated project sets htmlTemplateEngine: 'njk', so Nunjucks parsed
    // them and `npx eleventy` died with "expected name as lookup value, got ."
    // having written 0 files: an all-features export could not be BUILT at all.
    // The delimiters are now concatenated at runtime (MF_OPEN/MF_CLOSE) and the
    // sample uses HTML entities, so no two-brace sequence reaches the emitted
    // page. Found by the addon demo harness, which is the first thing to build
    // and serve an all-features export since ES-1 broke `test:e2e:all`.
    version: '1.16.0',
    label: 'E-Sign / Contracts',
    // Renders an extras slot under this toggle in the Export Settings modal,
    // filled by ui/esign-templates-panel.js — the same seam PWA Install uses.
    // Contract templates authored there are SEEDED into the exported site's
    // contract_templates table, where the site owner can then edit them.
    hasExtras: true,
    description:
      'Digital contract signing with OTP verification, canvas signature, and hash-chain audit trail',
    default: false,
    usesD1: true,
    usesR2: true,
    requiresAll: ['portal'],
    requiresAny: [],
    // 'cron' is SOFT on purpose. Everything the scheduler adds is recovery and
    // upkeep, never the signing path: expiry is derived at read time,
    // completion retries on read and from an admin button, and anchoring has
    // Anchor-now buttons. A site without the scheduler signs contracts
    // correctly — it just does the automatic half by hand, and the CRM says so.
    softRequires: ['messaging', 'cron'],
    // Enforced by validateFeatures(). Flip `signed` to true ONLY in the same
    // commit that fills in the signature line in PACK-STATUS.md — the two are
    // one decision, and a code flag without the countersigned document is
    // exactly the state this gate exists to prevent.
    legalGate: {
      signed: false,
      doc: 'builder/scripts/export/PACK-STATUS.md § Legal/Compliance Gate'
    }
  },
  {
    key: 'invoicing',
    // 1.2.1: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.3.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.4.0 (ES-6): RESEND_API_KEY became target 'both' so the scheduler can send
    // e-sign reminders on the SAME transport as every other signer-facing
    // email. That changes the setup surfaces (.dev.vars, SETUP.html) this
    // pack contributes to; the pack itself did not change, and ownership is
    // proven from emitted bytes.
    version: '1.5.0',
    label: 'Invoicing',
    description: 'Invoice creation, Stripe checkout, and payment tracking',
    default: false,
    usesD1: true,
    usesR2: false,
    requiresAll: ['portal'],
    requiresAny: [],
    softRequires: ['pushNotifications']
  },
  {
    key: 'media',
    // 1.4.2: carries the shared confirmation-receipt/Turnstile setup warning.
    // 1.5.0 (ES-3): the shared wrangler.toml scaffold documented a secret that no
    // longer exists (BROWSERLESS_API_KEY, read by nothing since the e-sign pack
    // shipped) and now names the Browser Rendering pair instead. That comment is
    // emitted into EVERY export, so this pack's bytes moved without this pack
    // changing — ownership is proven from emitted bytes, so the version follows.
    // 1.6.0 (ES-6): RESEND_API_KEY became target 'both' so the scheduler can send
    // e-sign reminders on the SAME transport as every other signer-facing
    // email. That changes the setup surfaces (.dev.vars, SETUP.html) this
    // pack contributes to; the pack itself did not change, and ownership is
    // proven from emitted bytes.
    version: '1.7.0',
    label: 'Media Delivery',
    description: 'File uploads, galleries, document delivery, and ZIP package downloads',
    default: false,
    usesD1: true,
    usesR2: true,
    requiresAll: ['portal'],
    requiresAny: [],
    softRequires: ['pushNotifications']
  },
  {
    key: 'pageViewTransitions',
    label: 'Page Transitions',
    description:
      'Smooth view transitions between pages (CSS-only, ~85% browser support, falls back to instant nav)',
    default: false,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: []
  },
  {
    key: 'hapticFeedback',
    label: 'Haptic Feedback',
    description:
      'Brief vibration on tap for touch devices (Android Chrome native; iOS Safari requires PWA install)',
    default: true,
    usesD1: false,
    usesR2: false,
    requiresAll: [],
    requiresAny: [],
    softRequires: ['pwa']
  }
];

/**
 * Version of the shared export machinery — everything the exporters emit that
 * is not owned by a versioned feature pack (scaffolds, generators, both
 * exporters, aggregate outputs, and every inline feature: cms, forms, pwa,
 * pwaInstall, sitemap, pageViewTransitions, hapticFeedback).
 *
 * The artifact-release gate (scripts/tests/artifact-release-gate.js) fails any
 * change to generated output that is not accompanied by the owning artifact's
 * version bump — this constant is the owning version for all non-pack output.
 * Semver, no wall-clock. See docs/fleet-contracts.md §2.
 */
// 1.15.0 -> 1.16.0 (2026-08-24): the active nav link is now marked with the
// TEMPLATE'S OWN convention rather than a hardcoded `cs-active`, and the marker
// is CLEARED from every other link before being set. Two byte changes follow
// for every export that has a nav: the active link gains `aria-current="page"`,
// and a marker the crawler froze onto a link that matches no page is no longer
// left behind. 12 of the 197 crawled templates mark the current page with
// something other than `cs-active` (7 `.active`, 4 `#active-menu`, 1 `#active`)
// and were shipping a class their own stylesheet never matched.
//
// 1.19.0 -> 1.20.0 (2026-08-24): export gate v2. EXPORT_GATE_VERSION went
// '1' -> '2', which is stamped in two emitted files — the export-report.html
// footer and pagestitch.lock.json's `exportGateVersion` — and the BLOCKING
// enforcement sentence in the report changed with it, because blocking no
// longer means "no bypass" on an interactive export. `template-hotlink-assets`
// also moved BLOCKING -> WARNING, which changes its row (severity + detail) in
// the report of any template-derived project.
//
// 1.23.0 -> 1.24.0 (2026-08-25): P9 freezes one snapshot before hash, audit and
// generation, so headless exports now ship that snapshot's full audit in
// export-report.html. P10 adds snapshot-bound publish-recognition disclosure.
//
// 1.24.0 -> 1.25.0 (2026-08-27): Deploy Assist DA-0. setup.js gains a step
// graph (each entry is [label, meta, fn] with ids, declared deps and a fatal
// flag), argv parsing, a non-secret .pagestitch-setup.json resume state, and
// --resume / --only / --skip / --dry-run / --plan-json / --keep-going. Two
// behaviour changes ride along, both fixes: a failed fatal step now STOPS the
// run instead of letting the pipeline deploy on top of failed infrastructure,
// and ask() no longer creates one readline interface per question -- the old
// shape never settled its promise on a closed stdin, so any non-TTY run exited
// mid-pipeline with no error and no state written. .gitignore gains the state
// file, which sGit would otherwise commit.
// 1.25.0 -> 1.26.0 (2026-08-27): Deploy Assist DA-1. setup.js splits secret
// COLLECTION from APPLICATION. A new always-run `collect` step resolves values
// from preseed > environment > .dev.vars > auto-generation and fills the map
// sSecrets and sScheduler both read; it is exempt from --resume/--only/--skip
// because it touches no remote state and skipping it is what let a resumed run
// deploy a Worker with no secrets. Adds --preseed and --non-interactive, reads
// .deploy-assist.json when present (bound to one export by slug + configHash;
// an explicit --preseed that does not apply is fatal, an auto-discovered one is
// ignored with a warning), preserves undeclared keys in .dev.vars instead of
// deleting them, and makes sActionsSecrets probe `gh secret list` before
// re-prompting.
// 1.26.0 -> 1.27.0 (2026-08-27): Deploy Assist DA-2. setup.js gains --verify, a
// READ-ONLY pass that checks a deployment and reports. Structured as pure
// judgements (judgeMissing / judgeTurnstilePairing / judgeAnalyticsHtml /
// judgeExpectedTables / parseNameList) over observed facts, plus thin adapters.
// It covers the two documented-but-unenforced traps directly: a Turnstile
// sitekey set without its secret (a widget that renders but never enforces) and
// the analytics placeholder still present in served HTML (a site collecting
// nothing). It never repairs what it finds, and the verify step is excluded
// from a normal run exactly as normal steps are excluded from it.
// 1.27.0 -> 1.28.0 (2026-08-27): Deploy Assist DA-3. --plan-json additionally
// reports siteDomain, githubRepo and companionWorkers. The operator tool needs
// the intended host to attach a custom domain and to scope a Turnstile widget,
// and it reads the plan rather than scraping the baked CONFIG out of setup.js
// source. None of the three is a secret; all are already visible in
// wrangler.toml and pagestitch.lock.json.
// 1.28.0 -> 1.29.0 (2026-08-28): Deploy Assist DA-10. SETUP.html is reworked and
// four defects in it are fixed: the banner's advertised click-to-copy threw a
// TypeError on every click since it was written (copyCode(btn) does
// btn.closest('.code-block') and that div has none); badge:'recommended' hit a
// two-arm ternary and rendered nothing; the progress key was READ from the
// interpolated slug but WRITTEN from the page title, agreeing only by accident
// of slug shape; and the clipboard promise had no catch. Every inline on*=
// handler is replaced by one delegated listener keyed on data- attributes, and
// window.confirm by an in-page confirmation -- so export/setup-guide.js comes
// OFF both ui-consistency-sweep allowlists, which shrink. The guide also gains
// a --verify step, a residual-manual list COMPUTED from features with a reason
// per entry, and a CMS callback URL derived from the real host rather than
// hardcoded to pages.dev. setup.js's "Still needed" list is computed the same
// way; several of its old entries are now automatable and were removed.
//
// 1.29.0 -> 1.31.0 (2026-08-28): Astro execution-audit fixes. The Astro target
// now keeps client bindings live through src/data/client.json, emits the shared
// auth/login/PWA/upload static asset folders it references, writes D1/R2
// bindings plus scheduler companion-worker config, ships setup.js/npm run init,
// consolidates supported pack admin tabs into /crm/, and joins the artifact
// release manifest as a schema-v3 target dimension.
//
// 1.31.0 -> 1.32.0 (2026-08-28): Deploy Assist execution-audit fixes. assist.mjs
// now calls the Stripe/Resend webhook APIs it already had, proves dashboard
// credentials before writing the preseed when a cheap probe exists, and counts
// GitHub Actions handoff values in its completion total. setup.js blocks Pages
// deploy on a failed scheduler Worker, verifies the latest Pages deployment
// status instead of merely seeing a deployment list, parses nested D1 JSON
// results, and checks manifest.webmanifest for PWA exports. SETUP.html escapes
// the derived deployment host and points Resend at the actual served host.
//
// 1.33.0 -> 1.34.0 (2026-08-28): Entrance animations EA-2. Builder-canonical
// scroll entrances become emitted output on all five surfaces. `data-pb-reveal`
// / `data-pb-reveal-delay` are stamped onto PAGE sections (never nav/footer,
// never a replaced element, never the LCP image's ancestor chain) from
// state.entrance and per-section overrides; a presence-gated stylesheet joins
// root.css; the shared scroll runtime gains a builder reveal row plus three
// explicit mode operations and the ephemeral `pb-reveal-armed` lifecycle; and
// `<html data-pb-entrance-template="off">` is emitted by generated documents
// when the project turns the template's own convention off. The ASTRO target
// additionally starts SHIPPING the scroll runtime at all -- `hasScrollAnimations`
// had been hardcoded false since that exporter was written, so every motion
// convention the other four surfaces animate was silently static there.
// 1.34.0 -> 1.35.0 (2026-08-28): Deploy Assist DA-11 — "receipt, not recipe".
// SETUP.html stops asking the reader what they did and reports what the machine
// found. setup.js gains `--verify --json` (human output moves to stderr so
// stdout carries only the document); the guide gains a hero with ONE command, a
// paste/drop sync strip that reconciles against that JSON, and steps ranked by
// WHO MUST ACT rather than by document order — a "What still needs you" group
// carrying the reason each item has no API, and everything else demoted to
// reference. Self-reported checkboxes, the percentage, the arbitrary 80%
// completion panel and confetti-on-claim are all removed; celebration now fires
// only when --verify reports zero failures. Steps carry stable ids and storage
// is keyed by them, so a re-export no longer re-maps saved state onto different
// steps. Step headers are real buttons with aria-expanded/aria-controls, the
// meter is a role=progressbar, targets meet 44px, and motion is opt-out.
// 1.35.0 -> 1.36.0 (2026-08-28): Deploy Assist DA-12 — the power-user and polish
// buckets from the SETUP.html design review. A sticky toolbar carries project
// context, filter chips (All / Needs you / Attention / Verified) and a search
// field; chips and query COMPOSE, and chips for states that do not exist stay
// hidden rather than being dead controls. '/' focuses search from anywhere,
// Escape clears it. Every step gains copy-all-commands and a permalink, and a
// step hash opens and scrolls to that step. Reset became UNDO — a 6s toast —
// rather than a confirmation, since clearing is trivially recoverable. Copy
// buttons are width-pinned so "Copied" no longer reflows the header, the
// needs-attention pulse fires exactly once, and forced-colors is handled.
// 1.39.0 -> 1.40.0 (2026-08-29): Issue Reporting TR-4 — the fix ANALYSIS
// document. `LLM/EXECUTION-RULES.md` now instructs an agent fixing a generator
// defect to write the detail to `.pagestitch/fixes/<ISO-date>-<slug>.md` in the
// SAME commit as its `.pagestitch/feedback.jsonl` index line, with a template
// that asks for root cause and — the part a one-sentence summary always loses —
// what the author could NOT determine. The JSONL line stays MANDATORY: it is
// capped at a summary on purpose (fleet/feedback.js treats those lines as
// hostile input rendered to a terminal), and a write-up with no index entry is
// invisible to fleet-scan. Also fixes a long-standing lie in the ASTRO export's
// README: it called generateReadme but never generateLLMDocs, so for its whole
// life it told the reader to "point your LLM at LLM/CLAUDE.md" — a file that
// export has never contained. `generateReadme` takes `llmDocs` (default true,
// so the 11ty README is byte-identical) and Astro passes false.
// 1.40.0 -> 1.41.0 (2026-08-29): a THIRD LLM doc,
// `LLM/CODESTITCH-STANDARDS.md`, ships in every 11ty export — the HTML/CSS
// house style every placed section obeys (id-scoped CSS, the four mobile-first
// breakpoints, clamp() sizing, 4pt spacing, `cs-` naming, the `<picture>` +
// object-fit background pattern, the dark-mode layer palette). It exists
// because the two existing docs told an agent how to avoid breaking the
// GENERATOR's contracts and nothing told it how to write markup that still
// stacks with its neighbours — the property that is the entire reason these
// sections came from a library. Static content, so it is genuinely additive:
// no pack's exclusive output changed and every pack's move in the matrix is
// aggregate-only, which the ratchet correctly attributes here. Sourced and
// cross-checked in `export/codestitch-standards.js` — see its header for the
// three sources and the rule that the measured corpus beats the published
// prose where they disagree. Measured against the regenerated matrix, the only
// emitted files that changed are the three under `LLM/` — the new one plus the
// two existing docs, which gained pointers to it — and the Astro deploy stamp
// `public/.well-known/pagestitch.json`, which embeds this very constant and so
// moves on any bump. Flat is byte-identical: `flatExclusive` shows no added,
// removed or changed path. No pack version was forced; every pack's move is
// aggregate-only.
// 1.41.0 -> 1.42.0 (2026-08-29): a VENDORED BYTE BEATS A HOTLINK in section and
// template CSS. `generators.js` (local, critical and page-bundle section CSS)
// and `asset-resolver.js` (the collection pass that builds the map those three
// rewrite against — they MUST agree, so both moved together) now consult the
// reference bundle before absolutizing a `url(/…)` to the source origin, and
// emit `pb-vendored://` when the bundle holds the file. The export machinery
// already resolved that scheme into `assets/images/` for fonts' sake; this only
// gives it more to resolve.
//
// It changes emitted bytes for any project whose template vendored anything:
// measured over the corpus, 12,739 of 13,081 `url()` references across 258 of
// 375 domains stop being live requests to somebody else's server and become
// files in the ZIP. It is byte-neutral where nothing vendored — the fallback is
// the OLD absolutization, character for character, which
// `template-support-css-tests.js` pins for null/undefined/{} maps.
//
// Occasioned by abshillbillygyros.com: its bare host stopped resolving, so the
// hero background became nothing and `#f5f3f3` hero text rendered white on
// white — while the image sat vendored on disk, unread, because the lookup
// asked for the bare host and the bundle was keyed under `www.`.
//
// The release manifest was not regenerated with the 1.42.0 change because the
// artifact-release gate was already red on that tree (byte-identical failure
// with it stashed), and regenerating from a failing state is the one thing that
// rule forbids. The 1.43.0 follow-up below closes the export-core manifest
// debt from a clean source snapshot.
//
// 1.42.0 -> 1.43.0 (2026-08-30): Decorative SVG top-edge bleed now emits the
// counterpart rule for `.pb-decorative-top`, so a project with
// `decorativeBleed` can export a shape that bleeds upward instead of staying
// pinned inside the section. The committed golden export fixture carries no
// decorative references, so the release manifest cannot observe the visual
// change directly; the version bump records the exporter-core behavior change
// rather than citing a blind gate as coverage.
//
// 1.43.0 -> 1.44.0 (2026-09-01): Selection Coherence widens structural logo
// recognition in the shared normalizer. Nine corpus anchors now normalize to
// logo slots during snapshot transforms, changing their emitted HTML bytes.
//
// 1.44.0 -> 1.45.0 (2026-09-02): SC-2 scheduled-content emission. A project
// with per-section schedule windows now exports, on all three targets, exact
// `<!--pbsched:<exportId>-->` marker pairs + a `data-pb-schedule` root
// attribute per scheduled section, the fail-open runtime gate file
// (js/schedule-gate.js), the flat-only pagestitch-schedule.json sidecar, and
// additive lock `artifactState.scheduleRules`. The committed golden fixture
// schedules nothing, so the release matrix's page bytes are blind to the
// change and only the lock's exporter-core entry moves — the bump records the
// exporter-core behavior change rather than citing a blind gate as coverage
// (the 1.43.0 precedent); export-schedule-tests.js is the byte-level gate.
//
// 1.45.0 -> 1.46.0 (2026-09-02): EA-7 per-element entrance emission. A project
// with `overrides.elements[eid].revealPreset` (+ optional revealDelay) now
// stamps EXACTLY those elements on all three targets — element mode supersedes
// the section/site preset, replaced elements hoist to their <picture>, the G5
// LCP ancestor strip still wins, and both field names are excluded from every
// element-CSS emitter (G11 pins them out of emitted bytes). The committed
// golden fixture carries no element entrance, so the release matrix's bytes
// are blind to the change and the bump records the behavior change; golden
// G9/G10/G11 are the byte-level gates. This same (uncommitted) release also
// carries EA-10: the shared scroll runtime gains `previewEntranceOnce` on
// `__PB_ENTRANCE__`, which DOES move every export's js/scroll-animations.js
// bytes — the gate caught the runtime edit landing after the first 1.46.0
// matrix regeneration ([exporter-core: output CHANGED, version same]), and the
// manifest was regenerated again under this one version rather than minting a
// second for a release nothing has shipped.
//
// 1.46.0 -> 1.47.0 (2026-09-02): Track EA audit correction. Exact element
// preview no longer walks to an arbitrary builder-animated ancestor when the
// requested nested/unconfigured element is not itself a participant. The only
// allowed upward relaxation is now the contracted replaced-element <img> to
// its stamped <picture> host. This changes scroll-animations.js on every export;
// the expanded 29-row browser matrix pins both the ancestor refusal and the
// picture-host success path.
//
// 1.47.0 -> 1.48.0 (2026-09-02): Track SC audit correction. The scheduled-
// content gate now preserves and restores each section root's authored inline
// display value AND priority instead of deleting it whenever the window is
// visible. The golden fixture schedules nothing, so the release matrix cannot
// observe the changed gate bytes; export-schedule-tests executes the emitted
// program across initial-visible and hidden-to-visible states.
//
// 1.48.0 -> 1.49.0 (2026-09-02): The "Made in PageStitch" badge carries the
// PageStitch logo — three stitched panels with the dashed stitch — instead of
// the placeholder "P" glyph, at 14x18 rather than 16x16. Both the badge markup
// and its stylesheet move (`.ps-badge-mark` loses a `color` no part of the new
// mark reads), so every page of every export changes, on all three targets.
//
// 1.50.0 -> 1.51.0 (2026-09-05): root.css no longer re-declares a template-
// DERIVED heading font. `generateRootCSS` emitted `h1..h6,.cs-title,.cs-h3,
// .cs-topper { font-family; font-weight: 700 }` whenever the heading family
// differed from the body family — including when both were merely read off
// the template on load (`fontSource === 'template'`) — and the forced weight
// bolded every heading of fourhorsemenmedia.com, a site designed at 300. The
// block is now skipped while the fonts are derived, matching the preview
// renderer's `pb-heading-font` gate. The golden fixture has no heading font,
// so the release matrix cannot observe the change; 11ty-export-tests pins it.
//
// 1.51.0 -> 1.52.0 (2026-09-05): a new optional site effect, momentum wheel
// scrolling (`siteEffects.smoothScroll`, data/site-effects/smooth-scroll.js),
// joins the 11ty export's site-effects bundle when a project enables it. Off
// by default and derived ON when the template's source ran Lenis, so no
// existing export changes; the golden fixture never enables it and the
// release matrix cannot observe the bytes. site-effects-phase-c-h-tests pins
// presence and absence.
//
// 1.52.1 -> 1.53.0 (2026-09-07, agnostic ingest AG-4): TWO changes to emitted
// bytes, and the first is a repair.
//
// (a) The export facade stopped applying template-literal escaping to section
// JS. `escapeForScript` doubled every backslash and escaped backticks and `${`
// on the way into an EXTERNAL `sections.js`, which no template literal ever
// read. Measured over the corpus: of the 8,742 sections carrying JS that
// parses, TEN produced a `sections.js` that does not — and a page links one
// such file, so each of those killed the JS of every section on the site. Any
// export containing one changes.
//
// (b) Behaviour attachment. A section stamped `data-pb-behaviour` now emits a
// facade IIFE even with EMPTY `js` (the skip became "neither js nor stamp"),
// the bundle carries the C-8 prelude plus each runtime, and
// `stripAllBuilderAttrs` preserves the stamp by exact name so the shipped
// runtime can find its host.
//
// Golden section H (a stamped `.w-tabs` with empty js) makes the matrix able to
// SEE this: without it the recorded hashes would not move and the bump would be
// taken on faith, which is what happened at 1.8.0 and is recorded there.
//
// 1.53.0 -> 1.54.0 (agnostic ingest AG-6, 2026-09-08): the behaviour bundle
// gains four runtimes (carousel, accordion, mobile-nav, nav-scroll) and the C-8
// prelude matches hosts by id AND variant and lets an ancestor control walk
// stop at the nearest level with hits. Golden section H now also carries a
// stamped `.swiper` host with empty js, so every export containing it changes
// and the matrix can see the bump rather than take it on faith.
// 1.54.0 -> 1.55.0 (AG-0..AG-8 audit remediation, findings 4 and 5, 2026-09-08):
// the per-instance bind call now passes the facade's OWN section root as a third
// argument — `__pbBindDoc(document, cfgs, __getSectionRoot())` — because export
// strips `data-pb-section` and `data-pb-instance`, so the C-8 boundary walk
// found nothing off-canvas and an exported carousel acquired the next button of
// the section below it. The prelude gains `__pbAriaOwns` / `__pbRoleControl`,
// which resolve an `aria-controls` relation inside that boundary instead of
// comparing id strings, so two sibling tracks sharing an id bind nothing.
//
// Every export containing a stamped section changes, and golden section H (the
// stamped `.w-tabs` and `.swiper` hosts added at 1.54.0) is what lets the matrix
// SEE it rather than take the bump on faith.
// 1.55.0 -> 1.56.0 (AG-0..AG-8 re-audit findings 5 and 6, 2026-09-08): the C-8
// prelude's ownership rules changed and the prelude is emitted into every
// stamped export. `__pbAriaOwns` now refuses a control that declares a relation
// to a DIFFERENT element (not only an unresolvable one), and `__pbRoleControl`
// refuses two resolving candidates for one role instead of falling through to
// the class selector — a fallback that picked whichever one happened to carry
// the class.
// 1.56.0 -> 1.57.0 (AG-12, 2026-09-16): `data/behaviours/mobile-nav.js` widened
// from one Webflow shape to a config-driven runtime — the menu found through
// `aria-controls` with the most-links ownership rule, an `open` token worn by
// root/trigger/menu, `hidden`/`aria-hidden` attribute states, generateNavJS's
// hamburger convention as data. The runtime text is emitted into every stamped
// export, and golden section H gained an `aria-toggle-open` nav so the matrix
// sees the change rather than taking the bump on faith.
// 1.57.0 -> 1.58.0 (2026-10-01): export gate v4. EXPORT_GATE_VERSION went
// '3' -> '4', stamped in the export-report.html footer and the lock's
// `exportGateVersion` (and so the deploy stamp's hash). The report's BLOCKING
// sentence now says a download always completes, and its stylesheet gains the
// `exportedAnyway` disposition pill. A UI download no longer refuses a
// non-recoverable finding; it ships it stamped `exportedAnyway`.
export const EXPORTER_CORE_VERSION = '1.58.0';

/**
 * The app's ability to READ a `data-pb-behaviour` stamp (agnostic ingest, C-7).
 *
 * A section whose interaction lives in a behaviour runtime is inert — or worse,
 * silently half-bound — in an app build that predates the reader. Uploading such
 * a section before deploying the app is therefore a data-loss-shaped mistake
 * that no version bump can detect, because a bump proves a value was WRITTEN,
 * never that it was deployed.
 *
 * `build-pages.js` publishes this value in `dist/app/build.json` at deploy time,
 * and `upload-data-to-r2.js` fetches that file and refuses stamped section data
 * while the LIVE value is lower. Bump it in the same phase that changes what a
 * reader must understand.
 *
 * 0 = NO READER. AG-0 shipped the constant and the refusal machinery.
 *
 * 1 = AG-4's reader (2026-09-07): `parseBehaviourStamp` is the one token reader,
 * `behaviour-loader.js` fetches `data/behaviours/<id>.js` and refuses to ship a
 * stamped section without it, and every render/export entry point awaits
 * `ensureBehaviourRuntimes(state)`. A build at 1 understands `<id>` and
 * `<id>:<variant>` tokens and binds through the C-8 owner-scoped prelude.
 *
 * WHAT THIS VALUE DOES NOT SAY: that the app carrying it is deployed. That is
 * the entire point of the deployed-reader gate — the uploader reads the LIVE
 * build's manifest, not this constant, because a bump proves a value was
 * written and never that anyone shipped it.
 *
 * 2 = the C-8 ownership prelude gains `__pbAriaOwns` and `__pbRoleControl`
 * (AG-0..AG-8 re-audit, 2026-09-08). `data/behaviours/carousel.js` CALLS
 * `ctx.roleControl`, so running it against a version-1 loader throws
 * `TypeError: ctx.roleControl is not a function` and leaves the host marked
 * bound — a dead carousel on a live site. The runtime tree and the prelude are
 * one artifact delivered by two channels: the prelude ships with the APP, the
 * runtime ships to R2. This constant is what stops the second from overtaking
 * the first.
 */
export const BEHAVIOUR_STAMP_READER_VERSION = 2;

/** Keys of the 11 versioned feature packs (entries carrying a `version`). */
export function getVersionedPackKeys() {
  return FEATURES.filter((f) => typeof f.version === 'string').map((f) => f.key);
}

/** Semver of a versioned pack, or null for inline features / unknown keys. */
export function getFeatureVersion(key) {
  const entry = FEATURES.find((f) => f.key === key);
  return typeof entry?.version === 'string' ? entry.version : null;
}

/** Build default features object from registry. */
export function getFeatureDefaults() {
  const defaults = {};
  for (const f of FEATURES) defaults[f.key] = f.default;
  return defaults;
}

/**
 * Validate feature dependencies.
 * @param {object} features — { key: boolean } map
 * @returns {{ errors: string[], warnings: string[] }}
 *   errors = hard blocks (requiresAll/requiresAny violated)
 *   warnings = informational (softRequires violated)
 */
export function validateFeatures(features, options = {}) {
  const errors = [];
  const warnings = [];

  for (const f of FEATURES) {
    if (features[f.key] !== true) continue; // only check enabled features

    // legalGate: a pack whose compliance review has not been signed off cannot
    // be exported. Before ES-1 this gate existed only as prose in PACK-STATUS.md
    // — the word "SHIP-BLOCKING" appeared in a markdown file that nothing read,
    // so the pack was one checkbox away from shipping into a production export
    // at any moment. It is a code path now.
    if (f.legalGate && f.legalGate.signed !== true && options.allowUnsignedLegalGate !== true) {
      errors.push(
        `"${f.label}" is blocked by an unsigned compliance gate (${f.legalGate.doc}). ` +
          `A human must sign off the listed items and set legalGate.signed = true in feature-registry.js.`
      );
    }

    // requiresAll: every listed feature must be enabled
    for (const req of f.requiresAll) {
      if (features[req] !== true) {
        const reqDef = FEATURES.find((r) => r.key === req);
        errors.push(`"${f.label}" requires "${reqDef?.label || req}"`);
      }
    }

    // requiresAny: at least one listed feature must be enabled
    if (f.requiresAny.length > 0) {
      const anyMet = f.requiresAny.some((req) => features[req] === true);
      if (!anyMet) {
        const names = f.requiresAny.map((k) => {
          const def = FEATURES.find((r) => r.key === k);
          return def?.label || k;
        });
        errors.push(`"${f.label}" requires at least one of: ${names.join(', ')}`);
      }
    }

    // softRequires: warn but don't block
    for (const req of f.softRequires) {
      if (features[req] !== true) {
        const reqDef = FEATURES.find((r) => r.key === req);
        warnings.push(`"${f.label}" works best with "${reqDef?.label || req}" enabled`);
      }
    }
  }

  return { errors, warnings };
}

/**
 * Registry-driven D1 check. New packs just set usesD1: true — no hardcoded list.
 * @param {object} features — { key: boolean } map
 * @returns {boolean}
 */
export function needsD1(features) {
  return FEATURES.some((f) => f.usesD1 && features[f.key] !== false);
}

/**
 * Registry-driven R2 check. New packs just set usesR2: true — no hardcoded list.
 * @param {object} features — { key: boolean } map
 * @returns {boolean}
 */
export function needsR2(features) {
  return FEATURES.some((f) => f.usesR2 && features[f.key] === true);
}

/**
 * Is this pack on, honouring its registry default when state says nothing?
 * `forms` is default:true, so an untouched project HAS a contact form.
 */
function featureEnabled(feature, features) {
  const value = features?.[feature.key];
  return value === undefined || value === null ? Boolean(feature.default) : value === true;
}

/**
 * Packs that cannot work on a purely static host, i.e. one-click publishing to
 * `<slug>.pagestitch.site`. Those sites are files in R2 served by a Worker —
 * there are NO Cloudflare Pages Functions, so `functions/api/form.js` does not
 * exist and a contact form would post into the void.
 *
 * Registry-driven (usesD1 || usesR2), so a new backend-backed pack is covered
 * the day it is added rather than the day someone remembers to update a list.
 *
 * This is why hosted publishing BLOCKS rather than warns: `forms` is
 * default:true, so shipping a client site with a silently discarding contact
 * form would be the common case, not the edge case.
 *
 * @param {object} features — { key: boolean } map
 * @returns {Array<{key: string, label: string, description: string}>}
 */
/**
 * Does this project actually CONTAIN a form the exporter would adopt?
 *
 * `staticIncompatibleFeatures` answers a question about FLAGS, and `forms` is
 * default:true — so an untouched project counts as form-bearing whether or not
 * a single `<form>` exists anywhere in it. That is why a brochure site with no
 * contact section was blocked from publishing alongside one that really would
 * have posted into the void.
 *
 * Detection mirrors `convertFormsForCloudflare` exactly, because adoption is
 * what makes a form the export's problem: a form carrying an explicit
 * third-party action (Formspree, a custom worker) is left alone by the exporter
 * and keeps working on a static host, so it is not a blocker here either.
 *
 * DOM-based for the same reason it is there: a string scan cannot tell a real
 * `<form>` from the characters appearing in copy, and misses uppercase tags.
 * Returns false when no parser is available rather than guessing — the caller
 * treats that as "nothing found", and the honest disclosure still ships.
 *
 * @param {object} state — builder state ({ pages: [{ sections: [{ html }] }] })
 * @returns {boolean}
 */
export function projectHasAdoptableForm(state) {
  const Parser = globalThis.DOMParser;
  if (!Parser) return false;
  const parser = new Parser();
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      const html = section?.html;
      if (!html || typeof html !== 'string') continue;
      // Cheap reject first — parsing every section of every page is not free.
      if (!/<form/i.test(html)) continue;
      let doc;
      try {
        doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      } catch {
        continue;
      }
      for (const form of doc.querySelectorAll('form')) {
        const action = (form.getAttribute('action') || '').trim();
        const isNetlify = form.hasAttribute('data-netlify') || form.hasAttribute('netlify');
        if (!isNetlify && action && action !== '/api/form') continue;
        return true;
      }
    }
  }
  return false;
}

export function staticIncompatibleFeatures(features = {}) {
  return FEATURES.filter((f) => (f.usesD1 || f.usesR2) && featureEnabled(f, features)).map(
    ({ key, label, description }) => ({ key, label, description })
  );
}
